import { createAction, props } from '@ngrx/store';
import {
  Document,
  DocumentMimeType,
  DocumentType,
  RequiredDocumentType,
} from '../models/document-type';

export interface StoreDocumentTypesAction {
  documentTypes: DocumentType[];
  entityTypeId?: string;
}

export interface StoreRequiredDocumentTypesAction {
  documentTypes: RequiredDocumentType[];
  workspaceTypeId: string;
}

export const noopAction = createAction('[Document] Noop');

export const storeAllowedDocumentTypes = createAction(
  '[Document] Store Allowed Document Types',
  props<{ workspaceId: string; documentTypes: DocumentType[] }>(),
);

export const loadingAllowedDocumentTypes = createAction(
  '[Document] Loading Allowed Document Types',
  props<{ workspaceId: string }>(),
);

export const removeDocuments = createAction(
  '[Document] Remove Documents',
  props<{ entityType: string; entityId: string; documentIds: string[] }>(),
);

export const removeAllDocuments = createAction(
  '[Document] Remove All Documents',
  props<{ entityType: string; entityId: string }>(),
);

export const allowedDocumentTypesError = createAction(
  '[Document] Allowed Document Types Error',
  props<{ workspaceId: string; error: any }>(),
);

export const storeEntityDocuments = createAction(
  '[Document] Store Entity Documents',
  props<{ entityType: string; entityId: string; documents: any[] }>(),
);

export const loadingEntityDocuments = createAction(
  '[Document] Loading Entity Documents',
  props<{ entityType: string; entityId: string }>(),
);

export const entityDocumentsError = createAction(
  '[Document] Entity Documents Error',
  props<{ entityType: string; entityId: string; error: any }>(),
);

export const storeAdditionalEntityDocuments = createAction(
  '[Document] Store Additional Entity Documents',
  props<{ entityType: string; entityId: string; documents: any[] }>(),
);

export const updateThreadtoEntityDocument = createAction(
  '[Document] Update Thread to Entity Document',
  props<{
    entityType: string;
    entityId: string;
    threadId: string;
    isResolved: boolean;
  }>(),
);

export const storeDocumentDict = createAction(
  '[Document] Store Document Dict',
  props<{ documentDict: any }>(),
);

export const loadingAdditionalEntityDocuments = createAction(
  '[Document] Loading Additional Entity Documents',
  props<{ entityType: string; entityId: string }>(),
);

export const additionalEntityDocumentsError = createAction(
  '[Document] Additional Entity Documents Error',
  props<{ entityType: string; entityId: string; error: any }>(),
);

export const storeDocumentTypes = createAction(
  '[Document] Store Document Types',
  props<StoreDocumentTypesAction>(),
);

export const loadingDocumentTypes = createAction(
  '[Document] Loading Document Types',
  props<{ entityTypeId?: string }>(),
);

export const documentTypesError = createAction(
  '[Document] Document Types Error',
  props<{ entityTypeId?: string; error: any }>(),
);

export const storeRequiredDocumentTypes = createAction(
  '[Document] Store Required Document Types',
  props<StoreRequiredDocumentTypesAction>(),
);

export const loadingRequiredDocumentTypes = createAction(
  '[Document] Loading Required Document Types',
  props<{ workspaceTypeId: string }>(),
);

export const requiredDocumentTypesError = createAction(
  '[Document] Required Document Types Error',
  props<{ workspaceTypeId: string; error: any }>(),
);

export const storeMimeTypes = createAction(
  '[Document] Store Mime Types',
  props<{ mimeTypes: DocumentMimeType[] }>(),
);

export const loadingMimeTypes = createAction('[Document] Loading Mime Types');

export const mimeTypesError = createAction(
  '[Document] Mime Types Error',
  props<{ error: any }>(),
);

export const uploadingDocument = createAction(
  '[Document] Uploading Document',
  props<{
    uuid: string;
    entityId: string;
    filename: string;
    documentName: string;
    additional: boolean;
    documentTypeId: string;
    documentTypeName: string;
    questionId?: string;
  }>(),
);

export const uploadDocumentProgress = createAction(
  '[Document] Upload Document Progress',
  props<{ uuid: string; progress: number; entityId: string }>(),
);

export const uploadDocumentError = createAction(
  '[Document] Upload Document Error',
  props<{ error: any; uuid: string; entityId: string }>(),
);

export const cancelDocumentUpload = createAction(
  '[Document] Cancel Document Upload',
  props<{ uuid: string; entityId: string }>(),
);

export const uploadDocumentSuccess = createAction(
  '[Document] Upload Document Success',
  props<{ uuid: string; entityId: string; document: Document | null }>(),
);

export const deletingDocument = createAction(
  '[Document] Deleting Document',
  props<{ documentId: string }>(),
);

export const deleteDocumentError = createAction(
  '[Document] Delete Document Error',
  props<{ error: any; documentId: string }>(),
);

export const deleteDocumentSuccess = createAction(
  '[Document] Delete Document Success',
  props<{
    documentId?: string;
    entityType: string;
    entityId: string;
    documentIds?: string[];
  }>(),
);

export const updatingDocument = createAction(
  '[Document] Updating Document',
  props<{ documentId: string }>(),
);

export const updateDocumentError = createAction(
  '[Document] Update Document Error',
  props<{ error: any; documentId: string }>(),
);

export const updateDocumentSuccess = createAction(
  '[Document] Updating Document Success',
  props<{ documentId: string; document?: Document }>(),
);

export const storeDocumentThumbnails = createAction(
  '[Document] Store Document Thumbnails',
  props<{ documentId: string; thumbnails: any }>(),
);

export const loadingDocumentThumbnails = createAction(
  '[Document] Loading Document Thumbnails',
  props<{ documentId: string }>(),
);

export const documentThumbnailsError = createAction(
  '[Document] Document Thumbnails Error',
  props<{ documentId: string; error: any }>(),
);

export const storeActiveDocument = createAction(
  '[Document] Store Active Document',
  props<{ document: Document }>(),
);

export const loadingAttributeTypes = createAction(
  '[Document] Loading Attribute Types',
);

export const storeAttributeTypes = createAction(
  '[Document] Store Attribute Types',
  props<{ attributeTypes: any }>(),
);

export const attributeTypesError = createAction(
  '[Document] Attribute Types Error',
  props<{ error: any }>(),
);

export const storeDocument = createAction(
  '[Document] Store Document',
  props<{ document: Document; workspaceId: string }>(),
);

export const clearUploadingDocuments = createAction(
  '[Documents] Clear Uploading Documents',
);

export const clearSingleUploadingDocument = createAction(
  '[Documents] Clear Single Uploading Document',
  props<{ uuid: string; entityId: string }>(),
);
